import { BeRow, BeStack, BeTable, type BeTableColumn, type BeTableRow, BeText } from "@beos-css/react/index.ts";
import type React from "react";
import { useState } from "react";
import iconBeide from "../../icons/beide.png";
import iconBeide32 from "../../icons/beide-32.png";
import iconDisk from "../../icons/disk.png";
import iconDisk32 from "../../icons/disk-32.png";
import iconFolder from "../../icons/folder.png";
import iconFolder32 from "../../icons/folder-32.png";
import iconPeople from "../../icons/people.png";
import iconPeople32 from "../../icons/people-32.png";
import iconTerminal from "../../icons/terminal.png";
import iconTerminal32 from "../../icons/terminal-32.png";

const columns: BeTableColumn[] = [
	{ id: "name", title: "Name", width: 200 },
	{ id: "size", title: "Size", width: 70, align: "right" },
	{ id: "kind", title: "Kind", width: 110 },
];

type TreeNode = {
	id: string;
	level: number;
	parent: string | null;
	icon?: BeTableRow["icon"];
	cells: Record<string, React.ReactNode>;
	expandable?: boolean;
};

const TREE: TreeNode[] = [
	{
		id: "boot",
		level: 0,
		parent: null,
		icon: { mini: iconDisk, large: iconDisk32 },
		expandable: true,
		cells: { name: "boot", size: "-", kind: "Disk" },
	},
	{
		id: "home",
		level: 1,
		parent: "boot",
		icon: { mini: iconFolder, large: iconFolder32 },
		expandable: true,
		cells: { name: "home", size: "-", kind: "Folder" },
	},
	{
		id: "people",
		level: 2,
		parent: "home",
		icon: { mini: iconPeople, large: iconPeople32 },
		cells: { name: "people", size: "180 KB", kind: "Application" },
	},
	{
		id: "config",
		level: 1,
		parent: "boot",
		icon: { mini: iconFolder, large: iconFolder32 },
		expandable: true,
		cells: { name: "config", size: "-", kind: "Folder" },
	},
	{
		id: "terminal",
		level: 2,
		parent: "config",
		icon: { mini: iconTerminal, large: iconTerminal32 },
		cells: { name: "Terminal", size: "256 KB", kind: "Application" },
	},
	{
		id: "beide",
		level: 2,
		parent: "config",
		icon: { mini: iconBeide, large: iconBeide32 },
		cells: { name: "BeIDE", size: "1.2 MB", kind: "Application" },
	},
];

export function TableTreeDemo() {
	const [treeSel, setTreeSel] = useState<string[]>(["people"]);
	const [expanded, setExpanded] = useState<Set<string>>(new Set(["boot", "home", "config"]));

	const visibleTree: BeTableRow[] = TREE.filter((n) => {
		let p = n.parent;
		while (p) {
			if (!expanded.has(p)) return false;
			const parentNode = TREE.find((x) => x.id === p);
			p = parentNode ? parentNode.parent : null;
		}
		return true;
	}).map((n) => ({
		id: n.id,
		icon: n.icon,
		cells: n.cells,
		level: n.level,
		expandable: n.expandable,
		expanded: expanded.has(n.id),
	}));

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	return (
		<section id="demo-tree">
			<h3>Table view -- hierarchical (BeTable tree)</h3>
			<p>
				The same BeTable with the R5 BOutlineListView tree: an 8px triangle latch + 10px-per-level indent, no dotted connector lines
				(authentic R5). Click a latch to expand/collapse. The other two copies opt into <code>treeLines</code> (a non-R5 connector-guide
				affordance) and <code>splitHeader</code>, which composes with the tree by counting the caret into the lead.
			</p>
			<BeRow wrap gap="row">
				{(
					[
						["default", {}, visibleTree],
						["treeLines", { treeLines: true }, visibleTree],
						["splitHeader", { splitHeader: true }, visibleTree],
						["no icons", {}, visibleTree.map(({ icon, ...row }) => row)],
					] as const
				).map(([label, variant, data]) => (
					<BeStack key={label} gap="tight">
						<BeText face="mini">{label}</BeText>
						<BeTable
							{...variant}
							columns={columns}
							rows={data}
							selectedIds={treeSel}
							onSelectItem={(id) => setTreeSel([id])}
							onToggleExpand={(id) => toggle(id)}
						/>
					</BeStack>
				))}
			</BeRow>
		</section>
	);
}
